import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { AuthTokens, AuthUser } from "@/types/api";

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  refreshToken: string | null;
  pendingMfaEmail: string | null;
  mfaEnrollmentRequired: boolean;
  _hasHydrated: boolean;

  setSession: (tokens: AuthTokens) => void;
  setMfaPending: (email: string | null) => void;
  setMfaEnrollmentRequired: (val: boolean) => void;
  clearSession: () => void;
  setHasHydrated: (state: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      pendingMfaEmail: null,
      mfaEnrollmentRequired: false,
      _hasHydrated: false,

      setSession: (tokens) =>
        set({
          user: tokens.user,
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          pendingMfaEmail: null,
          mfaEnrollmentRequired: false,
        }),

      setMfaPending: (email) => set({ pendingMfaEmail: email }),
      setMfaEnrollmentRequired: (val) => set({ mfaEnrollmentRequired: val }),

      clearSession: () =>
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          pendingMfaEmail: null,
          mfaEnrollmentRequired: false,
        }),

      setHasHydrated: (state) => set({ _hasHydrated: state }),
    }),
    {
      name: "pathway.auth",
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        user: s.user,
        refreshToken: s.refreshToken,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    },
  ),
);

export const selectIsAuthenticated = (s: AuthState): boolean =>
  !!s.user && !!s.refreshToken;

export const selectHasHydrated = (s: AuthState): boolean => s._hasHydrated;
